import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Mail, Phone, Facebook, Instagram } from 'lucide-react';
import logoVagas from '../img/logo-vagas-branca.svg';
import { settingsService, type PublicSettings } from '../services/settings.service';
import { authService } from '../services';
import logoh2k from '../img/h2k-logo.png';

const PAINEL_URL = import.meta.env.VITE_PAINEL_URL || 'https://vagasbrusque.com.br';

export default function Footer() {
  const [settings, setSettings] = useState<PublicSettings>({});
  const isCompany = authService.isAuthenticated() && authService.isCompany();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const s = await settingsService.getPublicSettings();
        setSettings(s || {});
      } catch {
        // ignore
      }
    })();
  }, []);

  const handleBuscarVagas = () => {
    navigate('/vagas');
    window.scrollTo(0, 0);
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo e Descrição */}
          <div className="col-span-1">
            <div className="mb-4">
              <img src={logoVagas} alt="Vagas Brusque" className="h-10" />
            </div>
            <p className="text-sm mb-4">
              Empregos em Brusque e Santa Catarina, com vagas atualizadas em diversas áreas e cargos. Encontre oportunidades de trabalho de forma rápida, simples e gratuita, em um portal feito para conectar empresas e candidatos com mais facilidade.
            </p>
          </div>

          {/* Links Institucionais */}
          <div>
            <h3 className="text-[#87ca1c] mb-4">Institucional</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/sobre" className="hover:text-white transition-colors">
                  Sobre nós
                </Link>
              </li>
              <li>
                <Link to="/como-funciona" className="hover:text-white transition-colors">
                  Como funciona
                </Link>
              </li>
              <li>
                <Link to="/termos" className="hover:text-white transition-colors">
                  Termos de uso
                </Link>
              </li>
              <li>
                <Link to="/privacidade" className="hover:text-white transition-colors">
                  Política de privacidade
                </Link>
              </li>
            </ul>
          </div>

          {/* Para Empresas e Candidatos */}
          <div>
            <h3 className="text-[#87ca1c] mb-4">Acesso Rápido</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <button onClick={handleBuscarVagas} className="hover:text-white transition-colors text-left">
                  Buscar vagas
                </button>
              </li>
              <li>
                <a href={`${PAINEL_URL}/login`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Anunciar vaga
                </a>
              </li>
              <li>
                <a href={`${PAINEL_URL}/cadastro`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Cadastre-se
                </a>
              </li>
              <li>
                <a href={`${PAINEL_URL}/login`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Login
                </a>
              </li>
              <li>
                <Link to="/contato" className="hover:text-white transition-colors">
                  Fale conosco
                </Link>
              </li>
            </ul>
          </div>

          {/* Contato */}
          <div>
            <h3 className="text-[#87ca1c] mb-4">Contato</h3>
            {isCompany ? (
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="size-4" />
                  <a
                    href={`mailto:${settings.support_email || settings.contact_email || 'contato@vagasbrusque.com.br'}`}
                    className="hover:text-white transition-colors"
                  >
                    {settings.support_email || settings.contact_email || 'contato@vagasbrusque.com.br'}
                  </a>
                </li>
                {settings.contact_phone && (
                  <li className="flex items-center gap-2">
                    <Phone className="size-4" />
                    <a
                      href={`tel:${settings.contact_phone}`}
                      className="hover:text-white transition-colors"
                    >
                      {settings.contact_phone}
                    </a>
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 mb-4">
                Faça login como empresa para ver nossos contatos.
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <a href="https://www.facebook.com/groups/vagasbrusque/?locale=pt_BR" target="_blank" rel="noopener noreferrer" className="bg-white p-2 rounded text-[#1877F2] hover:bg-[#6CA7F5] transition-colors">
                <Facebook className="size-4" />
              </a>
              <a href="https://www.instagram.com/vagasbrusque.com.br?igsh=cHY1eGxvajhjaWxt" target="_blank" rel="noopener noreferrer" className="bg-white p-2 rounded text-[#E4405F] hover:bg-[#ED728B] transition-colors">
                <Instagram className="size-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-sm flex flex-col md:flex-row justify-between items-center gap-2">
          <p>+Q Marketing Ltda - CNPJ 15.390.889/0001-38.</p>
          <a href="https://h2k.com.br" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-white text-gray-900 px-3 py-1.5 rounded hover:opacity-90 transition-opacity">
            <span className="text-sm">Desenvolvido com</span>
            <span className="text-red-500">&#10084;</span>
            <span className="text-sm">por</span>
            <img src={logoh2k} alt="H2K" className="h-6" />
          </a>
        </div>
      </div>
    </footer>
  );
}
